import React, { Fragment, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Form from './Form';
import FrontCanvas from './FrontCanvas';
import './StaticEditor.scss';
import { Helmet } from 'react-helmet';
import Layout from './../Layout';
import * as selectors from './StaticEditor.selectors';
import * as actions from './StaticEditor.actions';
import { stringifyEqualityCheck } from './../../utils';
import * as loginSelectors from './../Login/Login.selectors';
import { Link } from 'react-router-dom';

const StaticEditor = (props) => {
    /* State to props */
    const templateCode = useSelector(selectors.getTemplateCode);
    const quantity = useSelector(selectors.getQuantity);
    const staticTemplate = useSelector(selectors.getStaticTemplate, stringifyEqualityCheck);
    const fetching = useSelector(selectors.getFetching);
    const addingToCart = useSelector(selectors.getAddingToCart);
    const printingOption = useSelector(selectors.getPrintingOption);
    const language = useSelector(selectors.getLanguage);
    const orientation = useSelector(selectors.getOrientation);
    const materialType = useSelector(selectors.getMaterialType);
    const size = useSelector(selectors.getSize);
    const type = useSelector(selectors.getType);
    const isFavorite = useSelector(selectors.getIsFavorite);
    const checkingFavorite = useSelector(selectors.getCheckingFavorite);
    const savingWork = useSelector(selectors.getSavingWork);
    const action = useSelector(selectors.getAction);
    const cxmlData = useSelector(loginSelectors.getCxmlData, stringifyEqualityCheck);

    /* Dispatch to props */
    const dispatch = useDispatch();
    const fetchStaticTemplate = useCallback((id) => dispatch(actions.fetchStaticTemplate(id)), [dispatch]);
    const setTemplateCode = useCallback((value) => dispatch(actions.setTemplateCode(value)), [dispatch]);
    const setQuantity = useCallback((value) => dispatch(actions.setQuantity(value)), [dispatch]);
    const setPrintingOption = useCallback((value) => dispatch(actions.setPrintingOption(value)), [dispatch]);
    const setLanguage = useCallback((value) => dispatch(actions.setLanguage(value)), [dispatch]);
    const setOrientation = useCallback((value) => dispatch(actions.setOrientation(value)), [dispatch]);
    const setMaterialType = useCallback((value) => dispatch(actions.setMaterialType(value)), [dispatch]);
    const setSize = useCallback((value) => dispatch(actions.setSize(value)), [dispatch]);
    const setType = useCallback((value) => dispatch(actions.setType(value)), [dispatch]);
    const resetState = useCallback(() => dispatch(actions.resetState()), [dispatch]);
    const addToCart = useCallback((orderType, history, type) => dispatch(actions.addToCart(orderType, history, type)), [dispatch]);
    const addFavorite = useCallback((history) => dispatch(actions.addFavorite(history)), [dispatch]);
    const checkFavorite = useCallback((templateCode) => dispatch(actions.checkFavorite(templateCode)), [dispatch]);
    const setAction = useCallback((value) => dispatch(actions.setAction(value)), [dispatch]);
    const loadVariables = useCallback(() => dispatch(actions.loadVariables()), [dispatch]);

    /* componentDidMount */
    useEffect(() => {
        resetState();
        setTemplateCode(props.match.params.id);
        fetchStaticTemplate(props.match.params.id);
        checkFavorite(props.match.params.id);

        if (parseInt(props.match.params.id) === 647) {
            setType('Framed Poster');
            setLanguage('English');
        } else if (parseInt(props.match.params.id) === 648) {
            setLanguage('English');
            setPrintingOption('color');
        } else if (parseInt(props.match.params.id) === 649) {
            setLanguage('English');
            setType('65# Uncoated Cover');
        } else if (parseInt(props.match.params.id) === 651) {
            setLanguage('English');
            setType('Pass');
        } else if (parseInt(props.match.params.id) === 652) {
            setPrintingOption('color');
        }

        if (props.match.params.action === 'edit') {
            loadVariables();
            setAction('edit');
        } else if (props.match.params.action === 'reorder') {
            loadVariables();
            setAction('reorder');
        }
    }, [setTemplateCode, resetState, checkFavorite, fetchStaticTemplate, loadVariables, setAction, setLanguage, setType, setPrintingOption,
        props.match.params.id, props.match.params.action]);

    let content = <div className="text-center"><i className="fas fa-circle-notch fa-spin" /> Loading data...</div>;
    if (!fetching) {
        content = (
            <div className="steps-content">
                <div className="left-side-container">
                    <Form
                        quantity={quantity}
                        printingOption={printingOption}
                        cxmlData={cxmlData}
                        templateCode={templateCode}
                        staticTemplate={staticTemplate}
                        fetching={fetching}
                        addingToCart={addingToCart}
                        language={language}
                        orientation={orientation}
                        materialType={materialType}
                        size={size}
                        type={type}
                        history={props.history}
                        productId={props.match.params.productId}
                        setQuantity={setQuantity}
                        setPrintingOption={setPrintingOption}
                        addToCart={addToCart}
                        setLanguage={setLanguage}
                        setOrientation={setOrientation}
                        setMaterialType={setMaterialType}
                        setSize={setSize}
                        setType={setType}
                        isFavorite={isFavorite}
                        addFavorite={addFavorite}
                        checkingFavorite={checkingFavorite}
                        savingWork={savingWork}
                        action={action}
                    />
                </div>
                <div className="right-side-container">
                    <FrontCanvas
                        templateCode={templateCode}
                        language={language}
                        type={type}
                        templateId={props.match.params.id}
                        staticTemplate={staticTemplate}
                        fetching={fetching}
                        printingOption={printingOption}
                    />
                </div>
            </div>
        );
    }

    let breadcrumbsContainer = null;
    if (parseInt(props.match.params.productId) === 5 && staticTemplate != null) {
        const breadcrumbs = [
            <Link to="/utp">Main Categories</Link>,
            ' / ',
            <Link to="/product/5">Forms Categories</Link>,
            ' / ',
            <Link to={'/department/5/' + staticTemplate.breadcrumbs[0].id}>{staticTemplate.breadcrumbs[0].label}</Link>,
            ' / ',
            staticTemplate.name,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 6 && staticTemplate != null) {
        const breadcrumbs = [
            <Link to="/utp">Main Categories</Link>,
            ' / ',
            <Link to="/product/6">COVID-19 Categories</Link>,
            ' / ',
            <Link to={'/department/6/' + staticTemplate.breadcrumbs[0].id}>{staticTemplate.breadcrumbs[0].label}</Link>,
            ' / ',
            staticTemplate.name,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    }

    return (
        <Layout
            title={<Fragment><i className="fas fa-paint-brush"></i> Editor</Fragment>}
            step={3}
            history={props.history}
        >
            <Helmet><title>Editor - UT Physicians</title></Helmet>
            <div className="static-editor-container">
                {breadcrumbsContainer}
                {content}
            </div>
        </Layout>
    );
};

export default StaticEditor;
